
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import BookingCard from "./BookingCard";
import { Loader2 } from "lucide-react";
import { useSpotOwner } from "@/hooks/useSpotOwner";

interface ParkingSpot {
  address: string;
  owner_name: string;
  price_per_hour: number;
  owner_id: string;
}

interface Booking {
  id: string;
  start_time: string;
  end_time: string;
  status: string;
  lot_count: number;
  parking_spot: ParkingSpot;
  overtime_hours: number;
  overtime_charges: number;
  actual_end_time: string | null;
}

const BookingsList = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchBookings = async () => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          *,
          parking_spot:parking_spots(
            address,
            owner_name,
            price_per_hour,
            owner_id
          )
        `)
        .in('status', ['confirmed', 'completed']) // Only fetch confirmed and completed bookings
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('Raw bookings data:', data);

      // Process bookings
      const processedBookings = data?.map(booking => {
        const startTime = new Date(booking.start_time);
        const endTime = new Date(booking.end_time);

        // If actual end time exists, calculate overtime
        let overtimeHours = booking.overtime_hours || 0;
        let overtimeCharges = booking.overtime_charges || 0;

        if (booking.actual_end_time && booking.status === 'completed') {
          const actualEnd = new Date(booking.actual_end_time);
          const scheduledEnd = new Date(booking.end_time);
          
          if (actualEnd > scheduledEnd) {
            const diffInHours = Math.ceil((actualEnd.getTime() - scheduledEnd.getTime()) / (1000 * 60 * 60));
            overtimeHours = diffInHours;
            overtimeCharges = diffInHours * booking.parking_spot.price_per_hour;
          }
        }

        return {
          id: booking.id,
          start_time: startTime.toISOString(),
          end_time: endTime.toISOString(),
          status: booking.status,
          lot_count: booking.lot_count || 1,
          parking_spot: booking.parking_spot,
          overtime_hours: overtimeHours,
          overtime_charges: overtimeCharges,
          actual_end_time: booking.actual_end_time
        } as Booking;
      });

      console.log('Processed bookings:', processedBookings);
      setBookings(processedBookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        title: "Error",
        description: "Failed to load bookings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSpotVacated = async (bookingId: string) => {
    const now = new Date();
    try {
      const { error } = await supabase
        .from('bookings')
        .update({ 
          status: 'completed',
          actual_end_time: now.toISOString(),
          updated_at: now.toISOString()
        })
        .eq('id', bookingId);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Parking spot marked as vacated",
      });
      
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast({
        title: "Error",
        description: "Failed to update booking status",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchBookings();

    // Subscribe to booking changes, but only for confirmed/completed bookings
    const channel = supabase
      .channel('booking-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: 'status=in.(confirmed,completed)'
        },
        (payload) => {
          console.log('Received booking update:', payload);
          fetchBookings();
        }
      )
      .subscribe();

    // Check for completed bookings every minute
    const intervalId = setInterval(fetchBookings, 60000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(intervalId);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No confirmed bookings found.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
      {bookings.map((booking) => (
        <BookingCard
          key={booking.id}
          booking={booking}
          onCancel={fetchBookings}
          onVacate={handleSpotVacated}
        />
      ))}
    </div>
  );
};

export default BookingsList;
